import React, { useState, useEffect, useRef } from 'react';

import Logo from '../images/logo.png';
import LoadingImg from '../images/loading.gif';
import * as Constants from '../Constants';
import IdInput from '../components/idInput';
import CameraCapture from '../components/cameraCapture';

/**
 * PILOT / INTERNAL TESTING PAGE — no login required.
 *
 * Exercises the self-hosted Face Match + Liveness pipeline end to end (NIN entry -> NIN
 * lookup -> Face Match selfie -> Liveness challenge -> result) against the no-auth
 * PublicVerificationController on the backend, so the pipeline can be proven out before it's
 * wired behind real bank-operator login and before unit billing is attached to it (billing
 * will be configured by the platform Admin/superadmin, priced per unit, separate from a
 * bank's own Com-Owner role — see PublicVerificationController's remarks).
 *
 * This page and its backend controller are both meant to be deleted (or locked down hard)
 * once the pipeline is confirmed working — do not link to this page from anywhere a real
 * customer could stumble onto it.
 *
 * ACCESS: every API call here requires the same "pilot access key" your backend admin set
 * in appsettings.json (Verification:PilotAccessKey). Enter it once below; it's kept only in
 * this browser's sessionStorage (cleared when the tab closes), never sent anywhere except as
 * the X-Pilot-Key header on these pilot calls.
 *
 * NO BANK SELECTION: per feedback that the bank list came back empty and isn't needed for this
 * test, there's no bank/company dropdown here. The backend (PublicVerificationController.Start)
 * auto-resolves a company's verification settings on its own, and auto-provisions sensible
 * pilot defaults if none exist yet — see ResolvePilotSetting there.
 */
function PilotVerify() {
    const [pilotKey, setPilotKey] = useState(() => sessionStorage.getItem('pilotKey') || '');
    const [nin, setNin] = useState('');

    const [step, setStep] = useState('nin'); // nin -> facematch -> liveness -> result
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');
    // Raw Youverify NIN lookup response (or the raw exception if the call itself failed), shown
    // when /start fails so you can see exactly what happened without console/log access. Only
    // PublicVerificationController sends this back — the real VerificationController never does.
    const [debugRaw, setDebugRaw] = useState(null);

    const [attemptId, setAttemptId] = useState(null);
    const [faceMatchMaxRetries, setFaceMatchMaxRetries] = useState(0);
    const [referencePhoto, setReferencePhoto] = useState(null);

    const [faceMatchResult, setFaceMatchResult] = useState(null);

    const [challenge, setChallenge] = useState(null); // {actions, attemptsRemaining}
    const [capturing, setCapturing] = useState(false);
    const [livenessResult, setLivenessResult] = useState(null);

    const [finalResult, setFinalResult] = useState(null);

    const cameraRef = useRef(null);

    useEffect(() => {
        sessionStorage.setItem('pilotKey', pilotKey || '');
    }, [pilotKey]);

    const callApi = async (path, method, body) => {
        const resp = await fetch(`${Constants.rooturl}/PublicVerification${path}`, {
            method,
            body: body ? JSON.stringify(body) : undefined,
            headers: {
                'Content-Type': 'application/json',
                'X-Pilot-Key': pilotKey,
            },
        });
        return resp.json();
    };

    const resetAll = () => {
        setStep('nin');
        setNin('');
        setErr('');
        setDebugRaw(null);
        setAttemptId(null);
        setFaceMatchMaxRetries(0);
        setReferencePhoto(null);
        setFaceMatchResult(null);
        setChallenge(null);
        setLivenessResult(null);
        setFinalResult(null);
    };

    // ---- Step 1: NIN ----
    const onStart = async (e) => {
        e.preventDefault();
        if (!pilotKey) {
            setErr('Enter the pilot access key first.');
            return;
        }
        setLoading(true);
        setErr('');
        setDebugRaw(null);
        try {
            // No bank/company selection for this pilot test — the backend auto-resolves (and, if
            // needed, auto-provisions) a company's verification settings on its own. See
            // PublicVerificationController.ResolvePilotSetting for why.
            const data = await callApi('/start', 'POST', {
                identifierType: 'NIN',
                identifier: nin,
            });
            setLoading(false);
            if (data.code === 0) {
                setAttemptId(data.attemptId);
                setFaceMatchMaxRetries(data.faceMatchMaxRetries);
                setReferencePhoto(data.referencePhoto);
                setStep('facematch');
            } else if (data.code === -20) {
                setErr('Pilot access key is missing or incorrect.');
            } else {
                setErr(data.status || 'Identity lookup failed.');
                if (data.debugRawNinResponse) setDebugRaw(data.debugRawNinResponse);
                else if (data.debugException) setDebugRaw(data.debugException);
            }
        } catch (e) {
            setLoading(false);
            setErr(Constants.serverErr);
        }
    };

    // ---- Step 2: Face Match ----
    const onCaptureFaceMatch = async () => {
        const frame = cameraRef.current?.captureFrame();
        if (!frame) {
            setErr('Camera is not ready yet.');
            return;
        }
        setLoading(true);
        setErr('');
        try {
            const data = await callApi(`/${attemptId}/facematch`, 'POST', { capturedImageBase64: frame });
            setLoading(false);
            if (data.code === 0) {
                setFaceMatchResult(data);
                if (data.match) {
                    setTimeout(() => setStep('liveness'), 1200);
                } else if (data.locked) {
                    loadFinalResult();
                }
            } else if (data.code === -20) {
                setErr('Pilot access key is missing or incorrect.');
            } else {
                setErr(data.status || 'Face match failed.');
                if (data.locked) loadFinalResult();
            }
        } catch (e) {
            setLoading(false);
            setErr(Constants.serverErr);
        }
    };

    // ---- Step 3: Liveness ----
    useEffect(() => {
        if (step === 'liveness' && !challenge) {
            loadChallenge();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    const loadChallenge = async () => {
        setLoading(true);
        setErr('');
        try {
            const data = await callApi(`/${attemptId}/liveness/challenge`, 'GET');
            setLoading(false);
            if (data.code === 0) {
                setChallenge(data);
                setLivenessResult(null);
            } else if (data.code === -20) {
                setErr('Pilot access key is missing or incorrect.');
            } else {
                setErr(data.status || 'Could not start the liveness check.');
                if (data.locked) loadFinalResult();
            }
        } catch (e) {
            setLoading(false);
            setErr(Constants.serverErr);
        }
    };

    const instructionFor = (action) => {
        if (action === 'blink') return 'Blink naturally';
        if (action === 'turnleft') return 'Slowly turn your head to your LEFT';
        if (action === 'turnright') return 'Slowly turn your head to your RIGHT';
        return action;
    };

    const onCaptureLiveness = async () => {
        if (!challenge) return;
        setCapturing(true);
        setErr('');
        try {
            // ~2.4s of frames — long enough to catch a natural blink or head turn, short enough
            // to keep the challenge snappy. Tune FRAME_COUNT/FRAME_INTERVAL_MS here if the real
            // model needs more/fewer frames to detect the gesture reliably (see
            // LandmarkLivenessService.cs on the backend for the detection thresholds themselves).
            const FRAME_COUNT = 12;
            const FRAME_INTERVAL_MS = 200;
            const frames = await cameraRef.current?.captureBurst(FRAME_COUNT, FRAME_INTERVAL_MS);
            setCapturing(false);

            if (!frames || frames.length === 0) {
                setErr('Could not capture frames from the camera.');
                return;
            }

            setLoading(true);
            const data = await callApi(`/${attemptId}/liveness/verify`, 'POST', {
                requestedActions: challenge.actions,
                framesBase64: frames,
            });
            setLoading(false);

            if (data.code === 0) {
                setLivenessResult(data);
                if (data.passed) {
                    setTimeout(() => loadFinalResult(), 1000);
                } else if (data.locked) {
                    loadFinalResult();
                }
            } else if (data.code === -20) {
                setErr('Pilot access key is missing or incorrect.');
            } else {
                setErr(data.status || 'Liveness check failed.');
                if (data.locked) loadFinalResult();
            }
        } catch (e) {
            setCapturing(false);
            setLoading(false);
            setErr(Constants.serverErr);
        }
    };

    const onRetryLiveness = () => {
        setChallenge(null);
        setLivenessResult(null);
        loadChallenge();
    };

    // ---- Step 4: Result ----
    const loadFinalResult = async () => {
        setLoading(true);
        try {
            const data = await callApi(`/${attemptId}/result`, 'GET');
            setLoading(false);
            if (data.code === 0) {
                setFinalResult(data.result);
                setStep('result');
            }
        } catch (e) {
            setLoading(false);
            setErr(Constants.serverErr);
        }
    };

    return (
        <div style={{ maxWidth: 480, margin: '24px auto', padding: 16 }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <img src={Logo} style={{ width: 160 }} alt="ID Verify" />
            </div>

            <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffe69c', borderRadius: 6, padding: 10, marginBottom: 16, fontSize: 13 }}>
                <strong>Pilot / internal testing only.</strong> No login is required on this page — do not
                share this link outside the team.
            </div>

            <div style={{ marginBottom: 16 }}>
                <IdInput
                    label="Pilot Access Key"
                    type="password"
                    value={pilotKey}
                    onChange={(e) => setPilotKey(e.target.value)}
                    placeholder="Matches Verification:PilotAccessKey in appsettings.json"
                />
            </div>

            {loading && (
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <img src={LoadingImg} style={{ width: 120 }} alt="Loading" />
                </div>
            )}

            {err && <div style={{ color: 'red', textAlign: 'center', fontWeight: 'bold', marginBottom: 16 }}>{err}</div>}

            {debugRaw && (
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 4 }}>
                        Raw Youverify response / exception (pilot debug only):
                    </div>
                    <pre style={{
                        fontSize: 11,
                        background: '#1e1e1e',
                        color: '#d4d4d4',
                        padding: 10,
                        borderRadius: 6,
                        maxHeight: 300,
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                    }}>
                        {(() => {
                            try {
                                return JSON.stringify(JSON.parse(debugRaw), null, 2);
                            } catch {
                                return debugRaw;
                            }
                        })()}
                    </pre>
                </div>
            )}

            {step === 'nin' && (
                <form onSubmit={onStart}>
                    <IdInput label="NIN" value={nin} onChange={(e) => setNin(e.target.value)} required />
                    <div style={{ textAlign: 'center', marginTop: 16 }}>
                        <button className="greenbutton" type="submit">Start Verification</button>
                    </div>
                </form>
            )}

            {step === 'facematch' && (
                <div style={{ textAlign: 'center' }}>
                    <h3>Step 2: Face Match</h3>
                    <p style={{ fontSize: 13 }}>Position your face in frame and capture a selfie to compare against the NIN record photo.</p>

                    <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                        {referencePhoto && (
                            <div>
                                <div style={{ fontSize: 12, marginBottom: 4 }}>NIN Record Photo</div>
                                <img
                                    src={referencePhoto.startsWith('data:') ? referencePhoto : `data:image/jpeg;base64,${referencePhoto}`}
                                    style={{ width: 140, height: 140, objectFit: 'cover', borderRadius: 8, border: '1px solid #ccc' }}
                                    alt="Reference"
                                />
                            </div>
                        )}
                        <div>
                            <div style={{ fontSize: 12, marginBottom: 4 }}>Live Camera</div>
                            <CameraCapture ref={cameraRef} />
                        </div>
                    </div>

                    {faceMatchResult && (
                        <div style={{ marginBottom: 12, fontSize: 14 }}>
                            <div><strong>Match:</strong> {faceMatchResult.match ? 'YES' : 'NO'}</div>
                            <div><strong>Confidence:</strong> {faceMatchResult.confidenceLevel}</div>
                            <div><strong>Retries remaining:</strong> {faceMatchResult.retriesRemaining}</div>
                        </div>
                    )}

                    <button className="greenbutton" onClick={onCaptureFaceMatch} disabled={loading}>
                        {faceMatchResult && !faceMatchResult.match ? 'Try Again' : 'Capture & Verify'}
                    </button>
                </div>
            )}

            {step === 'liveness' && (
                <div style={{ textAlign: 'center' }}>
                    <h3>Step 3: Liveness Check</h3>

                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                        <CameraCapture ref={cameraRef} />
                    </div>

                    {challenge && !capturing && (
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ fontWeight: 'bold', fontSize: 16 }}>
                                {challenge.actions.map(instructionFor).join(' — then — ')}
                            </div>
                            <div style={{ fontSize: 12, marginTop: 4 }}>Attempts remaining: {challenge.attemptsRemaining}</div>
                        </div>
                    )}

                    {capturing && <div style={{ marginBottom: 16, fontWeight: 'bold' }}>Recording... perform the action now</div>}

                    {livenessResult && (
                        <div style={{ marginBottom: 12, fontSize: 14 }}>
                            <div><strong>Passed:</strong> {livenessResult.passed ? 'YES' : 'NO'}</div>
                            <div><strong>Actions confirmed:</strong> {(livenessResult.actionsConfirmed || []).join(', ') || 'none'}</div>
                            <div><strong>Retries remaining:</strong> {livenessResult.retriesRemaining}</div>
                        </div>
                    )}

                    {livenessResult && livenessResult.debugMetrics && (
                        <div style={{ marginBottom: 16, textAlign: 'left' }}>
                            <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 4 }}>
                                Detection debug (pilot only) — per-frame yaw/EAR values behind the pass/fail above:
                            </div>
                            <pre style={{
                                fontSize: 11,
                                background: '#1e1e1e',
                                color: '#d4d4d4',
                                padding: 10,
                                borderRadius: 6,
                                maxHeight: 300,
                                overflow: 'auto',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                            }}>
                                {(() => {
                                    try {
                                        return JSON.stringify(JSON.parse(livenessResult.debugMetrics), null, 2);
                                    } catch {
                                        return livenessResult.debugMetrics;
                                    }
                                })()}
                            </pre>
                        </div>
                    )}

                    {challenge && (
                        <button className="greenbutton" onClick={onCaptureLiveness} disabled={loading || capturing}>
                            {livenessResult && !livenessResult.passed ? 'Try Again' : 'Start Liveness Capture'}
                        </button>
                    )}
                    {livenessResult && !livenessResult.passed && !capturing && (
                        <button style={{ marginLeft: 8 }} onClick={onRetryLiveness}>New Challenge</button>
                    )}
                </div>
            )}

            {step === 'result' && finalResult && (
                <div style={{ textAlign: 'center' }}>
                    <h3>Result</h3>
                    <table style={{ margin: '0 auto', fontSize: 14 }}>
                        <tbody>
                            <tr><td style={{ textAlign: 'right', paddingRight: 8 }}>Overall</td><td><strong>{finalResult.overallStatus}</strong></td></tr>
                            <tr><td style={{ textAlign: 'right', paddingRight: 8 }}>NIN Lookup</td><td>{finalResult.ninLookupStatus}</td></tr>
                            <tr><td style={{ textAlign: 'right', paddingRight: 8 }}>Face Match</td><td>{finalResult.faceMatchStatus} ({finalResult.faceMatchConfidence})</td></tr>
                            <tr><td style={{ textAlign: 'right', paddingRight: 8 }}>Liveness</td><td>{finalResult.livenessStatus}</td></tr>
                            {finalResult.failureReason && (
                                <tr><td style={{ textAlign: 'right', paddingRight: 8 }}>Reason</td><td>{finalResult.failureReason}</td></tr>
                            )}
                        </tbody>
                    </table>
                    <div style={{ marginTop: 16 }}>
                        <button className="greenbutton" onClick={resetAll}>Start Over</button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PilotVerify;
